import type { MetricSummary, SliceMetrics } from '@/analysis/searchContract'
import { getSevColor } from '@/constants'

/**
 * Pipeline-stage latency waterfall for one stream. Horizontal bars = per-stage
 * latency (ms) on a shared scale for the focused stream’s current metrics.
 * Localizes stress (e.g. long decode + short download ⇒ decode/CPU, not network).
 * Download is a kbps chip, not a bar.
 *
 * Stage sources (honest labeling):
 *  - Download: kbps (not ms) — chip, colored by its own tier.
 *  - Demux→Decode: demuxToDecodeLag (real per-frame lag).
 *  - Decode: decodeQueue (live WS) with packetDelay as persisted proxy.
 *  - Glass-to-glass: mediaLatency — "n/a" until the player emits it.
 *  - Render ms is unwired (SliceMetrics has no renderLatency) — omitted.
 */

type Stage = {
  key: string
  label: string
  ms: number | null
  tier: 0 | 1 | 2 | null
  note?: string
}

const num = (m: MetricSummary | undefined | null): number | null =>
  typeof m?.displayValue === 'number' && Number.isFinite(m.displayValue) ? m.displayValue : null

const tierOf = (m: MetricSummary | undefined | null): 0 | 1 | 2 | null =>
  m?.tier === 0 || m?.tier === 1 || m?.tier === 2 ? m.tier : null

export function waterfallStages(metrics: SliceMetrics | null | undefined): Stage[] {
  const m = metrics ?? ({} as SliceMetrics)
  const decode = m.decodeQueue && num(m.decodeQueue) != null ? m.decodeQueue : m.packetDelay
  const decodeIsProxy = !(m.decodeQueue && num(m.decodeQueue) != null)
  return [
    {
      key: 'demuxDecode',
      label: 'Demux → Decode',
      ms: num(m.demuxToDecodeLag),
      tier: tierOf(m.demuxToDecodeLag),
    },
    {
      key: 'decode',
      label: 'Decode (queue+worker)',
      ms: num(decode),
      tier: tierOf(decode),
      note: decodeIsProxy ? 'proxy' : undefined,
    },
    {
      key: 'glass',
      label: 'Glass-to-glass',
      ms: num(m.mediaLatency),
      tier: tierOf(m.mediaLatency),
      note: num(m.mediaLatency) == null ? 'n/a — player does not emit it yet' : undefined,
    },
  ]
}

const fmtMs = (v: number | null) =>
  v == null ? 'n/a' : v < 10 ? `${v.toFixed(1)}ms` : `${Math.round(v)}ms`

export default function LatencyWaterfall({ metrics, title }: {
  metrics: SliceMetrics | null | undefined
  title?: string
}) {
  const stages = waterfallStages(metrics)
  const maxMs = Math.max(1, ...stages.map((s) => s.ms ?? 0))
  const download = metrics?.downloadKbps
  const downloadKbps = num(download)
  const downloadTier = tierOf(download)

  return (
    <div style={{ border: '0.5px solid var(--bd-1)', borderRadius: 'var(--radius)', padding: '10px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--t-1)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
          {title ?? 'Pipeline latency'}
        </span>
        <span
          title="Sustained download throughput (input side of the pipeline), colored by its own tier."
          style={{
            marginLeft: 'auto', fontSize: 10, fontWeight: 600,
            color: downloadTier == null ? 'var(--t-2)' : getSevColor(downloadTier),
          }}
        >
          ↓ {downloadKbps == null ? 'n/a' : `${Math.round(downloadKbps)} kbps`}
        </span>
      </div>
      <div style={{ fontSize: 10, color: 'var(--t-2)', marginBottom: 8 }}>
        Stage latency (ms) for selected stream
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {stages.map((s) => {
          const w = s.ms == null ? 0 : Math.max(2, (s.ms / maxMs) * 100)
          const color = s.tier == null ? 'var(--bd-1)' : getSevColor(s.tier)
          return (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 130, fontSize: 10, color: 'var(--t-1)', flexShrink: 0 }}>
                {s.label}
                {s.note ? <span style={{ color: 'var(--t-2)', marginLeft: 4, fontStyle: 'italic' }}>({s.note})</span> : null}
              </span>
              <div style={{ flex: 1, height: 10, background: 'var(--bg-3)', borderRadius: 3, overflow: 'hidden' }}>
                {s.ms != null && (
                  <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s' }} />
                )}
              </div>
              <span style={{ width: 52, fontSize: 10, fontWeight: 600, textAlign: 'right', color: s.ms == null ? 'var(--t-2)' : 'var(--t-0)', fontStyle: s.ms == null ? 'italic' : undefined }}>
                {fmtMs(s.ms)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
